import React from 'react';

import {observer} from 'mobx-react';

import {useStores} from '../../../Hooks';
import {DATA_INTERVAL} from '../../../MobxStores/DataStore';
import {permissionsEnum, procedureTypeEnum} from '../../../serverClientShared/sharedEnums';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import LimitedTextField from '../../Shared/LimitedTextField';
import SettingsThumbnail from '../SettingsShared/SettingsThumbnail';
import ConnectedModelsSection from './ConnectedModelsSection';
import PrivacySection from '../SettingsShared/PrivacySection';
import AIInstructionsSection from './AIInstructionsSection';
import procedureUtility from '../../../Utilities/ProcedureUtility';

const inputWrapClass = 'block w-full box-border px-3 py-2 rounded-lg border border-panels-normal transition-colors focus-within:border-classic-button-normal focus-within:border-2 focus-within:px-[11px] focus-within:py-[7px]';
const sectionClass = 'px-5 py-4 flex flex-col gap-4 border-t border-panels-normal first:border-t-0';
const labelClass = 'block mb-1 text-[11px] font-semibold text-main-normal tracking-[0.5px]';
const textareaClass = 'w-full block bg-transparent border-0 outline-none resize-none p-0 text-sm text-main-normal box-border';

const ItemSettingsTab = observer(({settings, threeDModels}) => {
    const {generalStore, projectStore, procedureStore, dataStore, modalStore} = useStores();
    const {
        procedure, setProcedure,
        editedName, setEditedName,
        editedDescription, setEditedDescription,
        editedAIInstructions, setEditedAIInstructions,
        editedThreeDModels, editedAllowRun, setEditedAllowRun,
        toggleThreeDModel: onToggleModel,
        isGeneratingAI,
        generateAIInstructions: onGenerateAI,
    } = settings;
    const isMedia = !!procedure?.MediaProcedureFile;
    const isDigitalTwin = procedureUtility.isDigitalTwin(procedure);
    const canGenerateWithAI = projectStore.checkAllowedCurrentProject(permissionsEnum.SUPPORT_AI_CHAT, false)
        && generalStore.domainSettings.IsSupportAIChatEnabled;
    const canChangePermissions = projectStore.checkAllowedCurrentProject(permissionsEnum.CREATE_PROCEDURE_AND_SETTINGS, false);
    const isAdminOrOwner = generalStore.userIsWorkspaceAdminOrOwner();
    const isRegular = procedure?.ProcedureType === procedureTypeEnum.REGULAR;
    const connectionDisabled = !isRegular;

    const handleCoverChange = async (imageId, thumbId) => {
        const saved = await procedureStore.addProcedureThumbnail(procedure.ProcedureIdentifier, imageId, thumbId);
        if (procedureStore.fetchedProceduresMap.size) procedureStore.fetchedProceduresMap.set(saved.ProcedureIdentifier, saved);
        const projectId = projectStore.currentProject?.ProjectIdentifier;
        const map = dataStore.projectsProceduresMap.get(projectId) || new Map();
        map.set(saved.ProcedureIdentifier, saved);
        dataStore.set('projectsProceduresMap', projectId, map);
        dataStore.set('projectsProceduresExpirationMap', projectId, Date.now() + DATA_INTERVAL);
        modalStore.modalData.procedure = saved;
        setProcedure(saved);
        generalStore.isLoaderOpen = false;
    };

    return (
        <div>
            <div className={sectionClass}>
                <div className="flex items-start gap-3">
                    <ConditionalComponent if={!isMedia}>
                        <SettingsThumbnail imageSrc={procedure?.coverImgThumb || procedure?.coverImg} onChange={handleCoverChange} />
                    </ConditionalComponent>
                    <div className="flex-1">
                        <label className={labelClass}>NAME</label>
                        <div className={inputWrapClass}>
                            <LimitedTextField
                                data-testid="procedure-name-input"
                                className={textareaClass}
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                readOnly={false}
                                multiline
                                isTextAreaAutoSize
                                originalValue={procedure?.ProcedureName}
                                limit={500}
                            />
                        </div>
                    </div>
                </div>

                <div>
                    <label className={labelClass}>
                        DESCRIPTION <span className="text-main-hover font-normal">(optional)</span>
                    </label>
                    <div className={inputWrapClass}>
                        <LimitedTextField
                            data-testid="procedure-description-input"
                            className={textareaClass}
                            placeholder={generalStore.translate('addADescription_withEllipsis')}
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            minRows={3}
                            maxRows={5}
                            multiline
                            readOnly={false}
                            isTextAreaAutoSize
                            originalValue={procedure?.ProcedureDescription}
                            limit={2000}
                        />
                    </div>
                </div>
            </div>

            <ConditionalComponent if={canGenerateWithAI}>
                <div className={sectionClass}>
                    <AIInstructionsSection
                        value={editedAIInstructions}
                        onChange={setEditedAIInstructions}
                        onGenerate={onGenerateAI}
                        isGenerating={isGeneratingAI}
                        originalValue={procedure?.AIInstructions}
                    />
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={isAdminOrOwner}>
                <div className={sectionClass}>
                    <PrivacySection settings={settings} />
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={isRegular && canChangePermissions}>
                <div className={sectionClass}>
                    <ConnectedModelsSection
                        threeDModels={threeDModels}
                        connectedModels={editedThreeDModels}
                        onToggleModel={onToggleModel}
                        allowRun={editedAllowRun}
                        onSetAllowRun={setEditedAllowRun}
                        disabled={connectionDisabled}
                    />
                </div>
            </ConditionalComponent>
        </div>
    );
});

export default ItemSettingsTab;
